// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { RootState } from '..';

import { getErrorMessage } from '@/utils/errors';
import { updateGamesCache } from '@/utils/game-info-api-cache';
import urlonStringify from '@/utils/urlon';

const DEBUG = false;

type FetchConfigProps = {
  ids: string[];
};

type FetchConfigResult = {
  data: Map<string, any[]>;
  success: boolean;
};

type FetchConfigError = {
  rejectValue: {
    error: string;
  };
};

export function onlyKeys(obj: any, includeKeys: string[]) {
  const result: any = {};
  Object.keys(obj).forEach((key: any) => {
    if (includeKeys.indexOf(key) > -1) {
      result[key] = obj[key];
    }
  });
  return result;
}

const apiUrl = window.config.dataSourceApiUrl;

const requestingSources: Record<string, boolean> = {};
const reqCache: Record<string, boolean> = {};

const checkIDSInCache = (ids: string[]) => {
  const notInCache: string[] = [];
  ids.forEach((id) => {
    if (!reqCache?.[id]) {
      notInCache.push(id);
      reqCache[id] = true;
    }
  });
  return notInCache;
};

const clearCacheForIDs = (ids: string[]) => {
  ids.forEach((id) => {
    if (reqCache?.[id]) {
      delete reqCache[id];
    }
  });
};

export const clearRequestSourcesCache = () => {
  Object.keys(reqCache ?? {}).forEach((key) => {
    delete reqCache[key];
  });
};
export const getRequestSourcesCache = () => {
  return reqCache;
};

export const fetchSources = createAsyncThunk<FetchConfigResult, FetchConfigProps, FetchConfigError>(
  'dataSources/list',
  async ({ ids }, { dispatch, getState, rejectWithValue }) => {
    // Declared outside the try so the catch can release the in-flight guards these lines set (below).
    let clearedIds: string[] = [];
    let qParams = '';
    try {
      const state: RootState = getState() as RootState;

      clearedIds = checkIDSInCache(ids);
      if (!clearedIds.length) {
        return { data: [], success: true };
      }

      qParams = urlonStringify({ ids: clearedIds });

      if (requestingSources[qParams]) {
        return { data: [], success: true };
      }
      requestingSources[qParams] = true;

      const authToken = window.config.guestToken ? window.config.guestToken : '';

      const response = await axios.get(`${apiUrl}/resolve/sources`, {
        headers: {
          Authorization: 'Bearer ' + authToken,
        },
        params: {
          q: qParams,
        },
        // This read always uses the GUEST token, never the owner session, so a 401 here is never evidence
        // the owner's access token expired. Opt out of the global auth recovery/teardown (auth-interceptor)
        // so a data-source 401 rejects to this thunk (handled below) instead of rotating the owner token,
        // replaying, failing again and signing the owner out (which it did on prod for the game browse).
        _owsNoAuthRecovery: true,
      });

      clearCacheForIDs(clearedIds);
      // Release the in-flight guard here (not only in the object branch below), so the non-object
      // ("no response set") reply also frees it — otherwise that id-set stays locked for the session.
      delete requestingSources[qParams];

      // Sometimes the API replies with "no response set" string resulting in each string characted to be added to the object in state
      if (response.data && typeof response.data === 'object') {
        const data = { ...response.data };

        delete data['__debug'];

        Object.keys(data ?? {}).forEach((key) => {
          if (data?.[key]?.element_type?.type_id === 'dynamic-page-element') {
            const noRefresh: any = [];
            const withRefresh: any = [];
            const playerBased: any = [];

            data?.[key]?.data.forEach((el: any) => {
              Object.keys(el ?? {}).forEach((k) => {
                if (el?.[k]?.dsId?.ref_id) {
                  const dsId = el?.[k]?.dsId?.ref_id;

                  if (!state.dataSources.items?.[dsId]) {
                    const target_player = el?.[k]?.dsId?.meta?.target === 'player';
                    const hasRefresh = el?.[k]?.dsId?.meta?.auto_refresh === true;

                    if (target_player) {
                      playerBased.push(dsId);
                    } else if (hasRefresh) {
                      withRefresh.push(dsId);
                    } else {
                      noRefresh.push(dsId);
                    }
                  }
                }
              });
            });

            if (playerBased?.length) dispatch(fetchSources({ ids: playerBased }));
            if (withRefresh?.length) dispatch(fetchSources({ ids: withRefresh }));
            if (noRefresh?.length) dispatch(fetchSources({ ids: noRefresh }));
          }
        });

        return { data: data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't fetch sources",
      });
    } catch (error) {
      // Release the in-flight guards so a failed set is retryable. Before the `_owsNoAuthRecovery` opt-out a
      // guest-token 401 never reached here (the interceptor handled it); now it rejects straight to this
      // catch, and without this the id stays marked-requested (reqCache) with `requestingSources[qParams]`
      // still set, so every later fetch short-circuits to empty and the source never re-loads this session.
      clearCacheForIDs(clearedIds);
      delete requestingSources[qParams];
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

export interface ConfigReducer {
  items: any;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export const dataSourcesSlice = createSlice({
  name: 'dataSources',
  initialState: <ConfigReducer>{
    items: {},
    loading: false,
    loaded: false,
    error: null,
  },
  reducers: {
    clearDataSources: (state) => {
      state.items = {};
      state.loading = false;
      state.loaded = false;
      state.error = null;
    },
    updateDataSourceJackpot: (state, action) => {
      Object.keys(state.items).forEach((key) => {
        if (
          key &&
          state.items[key] &&
          state.items[key].element_type &&
          state.items[key].element_type.type_id === 'module.jackpotV2Group'
        ) {
          const groupUpdate = action.payload.data;
          const data = [...JSON.parse(JSON.stringify(state.items[key].data))];

          groupUpdate.forEach((group: any) => {
            const groupIndex = data.findIndex((ob) => {
              return ob.group === group.group;
            });

            if (groupIndex > -1) {
              group.jackpots.forEach((jackpot: any) => {
                const jackpotIndex = data[groupIndex].jackpots.data.findIndex((el: any) => {
                  return el.id === jackpot.id;
                });

                if (jackpotIndex > -1) {
                  const name = data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.name;
                  let value = data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.value;

                  let amount =
                    data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.amounts?.[0]?.amount;

                  jackpot.accumulator_states.find((j: any) => {
                    if (j.name === name) {
                      value = j.value;
                      amount = j.amounts?.[0]?.amount;
                      return true;
                    }
                    return false;
                  });

                  data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.value = value;

                  if (data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.amounts?.length) {
                    data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.amounts[0].amount = amount;
                  }

                  data[groupIndex].jackpots.data[jackpotIndex] = {
                    ...data[groupIndex].jackpots.data[jackpotIndex],
                    ...onlyKeys(data[groupIndex].jackpots.data[jackpotIndex], [
                      'state',
                      'state_time',
                      'start_date',
                      'win_trigger_type',
                      'guaranteed_win',
                      'win_date',
                      'win_value',
                      'largest_win_amount',
                      'largest_win_date',
                      'largest_win_player',
                      'last_win_amount',
                      'last_win_date',
                      'last_win_player',
                    ]),
                  };
                }
              });
            }
          });

          state.items[key].data = data;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSources.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;

        if (action.payload.success) {
          state.items = { ...state.items, ...action.payload.data };
        }

        if (action.payload.data) {
          Object.keys(action.payload.data).forEach((sId: any) => {
            const ds = action.payload.data[sId];
            if (ds != null && ds.element_type?.type_id === 'slot_game') {
              updateGamesCache(ds.data, sId);
            }
          });
        }

        DEBUG && console.log('fetchSources.fulfilled', action.payload);
      })
      .addCase(fetchSources.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;

        DEBUG && console.log('fetchSources.rejected', action.payload);
      });
  },
});

export const { clearDataSources, updateDataSourceJackpot } = dataSourcesSlice.actions;

export default dataSourcesSlice.reducer;
